import { lumi } from "@/lib/lumi"
import {Check, Copy} from 'lucide-react'
import { useState, type FormEvent, type HTMLAttributes } from "react"
import toast from "react-hot-toast"

const UNIT_PRICE = 1680

const bankInfo = [
  { label: "銀行代碼", value: "822" },
  { label: "銀行名稱", value: "中國信託商業銀行" },
  { label: "戶名", value: "零加壹文創整合有限公司" },
  { label: "帳號", value: "303540352643" },
]

type FormState = {
  name: string
  phone: string
  email: string
  quantity: number
  transferDate: string
  transferAmount: string
  accountLastFive: string
  recipientName: string
  recipientPhone: string
  storeName: string
  storeAddress: string
  note: string
}

const emptyForm: FormState = {
  name: "",
  phone: "",
  email: "",
  quantity: 1,
  transferDate: "",
  transferAmount: "",
  accountLastFive: "",
  recipientName: "",
  recipientPhone: "",
  storeName: "",
  storeAddress: "",
  note: "",
}

async function copyText(value: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    /* fallback below */
  }
  try {
    const input = document.createElement("textarea")
    input.value = value
    input.setAttribute("readonly", "true")
    input.style.position = "fixed"
    input.style.opacity = "0"
    document.body.appendChild(input)
    input.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(input)
    return ok
  } catch {
    return false
  }
}

function OrderForm() {
  const [form, setForm] = useState<FormState>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [copiedKey, setCopiedKey] = useState("")
  const totalAmount = form.quantity * UNIT_PRICE

  const update = (key: keyof FormState, value: string | number) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return

    const transferAmount = Number.parseInt(form.transferAmount, 10)
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("請完整填寫訂購人資料")
      return
    }
    if (!form.transferDate || Number.isNaN(transferAmount) || form.accountLastFive.trim().length !== 5) {
      toast.error("請完整填寫匯款確認資料")
      return
    }
    if (!form.recipientName.trim() || !form.recipientPhone.trim() || !form.storeName.trim() || !form.storeAddress.trim()) {
      toast.error("請完整填寫 7-ELEVEN 店到店資訊")
      return
    }

    setSubmitting(true)
    const now = new Date().toISOString()
    const orderPayload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      quantity: form.quantity,
      transferDate: form.transferDate,
      transferAmount,
      accountLastFive: form.accountLastFive.trim(),
      recipientName: form.recipientName.trim(),
      recipientPhone: form.recipientPhone.trim(),
      storeName: form.storeName.trim(),
      storeAddress: form.storeAddress.trim(),
      note: form.note.trim(),
      totalAmount,
      status: "pending",
      creator: "guest",
      createdAt: now,
      updatedAt: now,
    }
    try {
      await lumi.functions.invoke("submitOrder", {
        method: "POST",
        body: orderPayload,
      })
      toast.success("訂單已送出，我們將核對匯款後安排出貨")
      setForm(emptyForm)
    } catch (error: unknown) {
      const err = error as { data?: { error?: string }; message?: string }
      toast.error(err?.data?.error || err?.message || "訂單送出失敗，請稍後再試或來電洽詢")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id="order" data-nav="dark" className="bg-[#f4efe6] px-8 py-20 md:px-10 md:py-28">
      <div className="mx-auto max-w-2xl">
        <p className="font-display text-sm tracking-[0.32em] text-slate-400">ORDER</p>
        <h2 className="mt-3 font-serif-tc text-3xl tracking-[0.18em] text-slate-800 md:text-4xl">立即訂購</h2>
        <p className="mt-4 text-[13px] leading-7 tracking-[0.08em] text-slate-500">
          請完成匯款後填寫以下資料，我們將依照付款順序安排出貨。
        </p>

        <form onSubmit={handleSubmit} className="mt-12 space-y-12">
          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">訂購人資料</legend>
            <Field label="姓名" value={form.name} onChange={(value) => update("name", value)} required />
            <Field label="手機號碼" value={form.phone} onChange={(value) => update("phone", value)} required inputMode="tel" />
            <Field label="Email" value={form.email} onChange={(value) => update("email", value)} required type="email" />
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">購買商品數量</legend>
            <div className="flex items-center justify-between border border-slate-200 bg-white/60 px-4 py-4">
              <div>
                <p className="font-serif-tc tracking-[0.12em] text-slate-700">微光 Inner Light</p>
                <p className="mt-1 text-[12px] tracking-[0.12em] text-slate-400">NT$ 1,680</p>
              </div>
              <div className="flex items-center border border-slate-300">
                <button
                  type="button"
                  className="h-10 w-10 text-slate-500"
                  onClick={() => update("quantity", Math.max(1, form.quantity - 1))}
                  aria-label="減少數量"
                >
                  −
                </button>
                <span className="w-8 text-center text-sm">{form.quantity}</span>
                <button
                  type="button"
                  className="h-10 w-10 text-slate-500"
                  onClick={() => update("quantity", form.quantity + 1)}
                  aria-label="增加數量"
                >
                  +
                </button>
              </div>
            </div>
            <p className="text-[12px] leading-6 text-slate-400">
              ※ 售價 NT$ 1,680 已含 7-ELEVEN 店到店運費，無須另加。
            </p>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">匯款資訊</legend>
            <div className="space-y-2 border border-slate-200 bg-[#eef3f6] px-3 py-3 sm:px-5 sm:py-5">
              {bankInfo.map((item) => {
                const isCopied = copiedKey === item.label
                return (
                  <button
                    key={item.label}
                    type="button"
                    onClick={async () => {
                      const ok = await copyText(item.value)
                      if (!ok) {
                        toast.error("複製失敗，請手動選取")
                        return
                      }
                      setCopiedKey(item.label)
                      toast.success(`已複製${item.label}`)
                      window.setTimeout(() => {
                        setCopiedKey((prev) => (prev === item.label ? "" : prev))
                      }, 1600)
                    }}
                    className="flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left text-[13px] text-slate-600 transition hover:bg-white/70"
                    aria-label={`複製${item.label}`}
                  >
                    <span className="w-[88px] shrink-0 text-slate-400">{item.label}</span>
                    <span className="min-w-0 flex-1 tracking-[0.06em]">{item.value}</span>
                    <span className="shrink-0 text-slate-400" aria-hidden="true">
                      {isCopied ? <Check size={15} strokeWidth={1.75} /> : <Copy size={15} strokeWidth={1.75} />}
                    </span>
                  </button>
                )
              })}
            </div>
            <p className="text-[12px] leading-6 text-slate-400">
              售價已含運費，匯款金額請依訂購組數計算。
              <br />
              請先完成匯款後再填寫以下資料。
            </p>
            <p className="text-[12px] leading-6 text-amber-800/80">
              ⚠️ 溫馨提示：轉帳時請勿填寫「備註」，以利專人為您快速核對帳款與安排出貨。
            </p>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">匯款確認</legend>
            <Field label="匯款日期" value={form.transferDate} onChange={(value) => update("transferDate", value)} required type="date" />
            <Field
              label="匯款金額"
              value={form.transferAmount}
              onChange={(value) => update("transferAmount", value)}
              required
              inputMode="numeric"
              placeholder={`${totalAmount}`}
            />
            <Field
              label="匯款帳號末五碼"
              value={form.accountLastFive}
              onChange={(value) => update("accountLastFive", value.replace(/\D/g, "").slice(0, 5))}
              required
              maxLength={5}
              inputMode="numeric"
            />
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">7-ELEVEN 店到店資訊</legend>
            <Field label="收件人姓名" value={form.recipientName} onChange={(value) => update("recipientName", value)} required />
            <Field label="收件人手機" value={form.recipientPhone} onChange={(value) => update("recipientPhone", value)} required inputMode="tel" />
            <Field label="7-ELEVEN 門市名稱" value={form.storeName} onChange={(value) => update("storeName", value)} required />
            <Field label="7-ELEVEN 門市地址" value={form.storeAddress} onChange={(value) => update("storeAddress", value)} required />
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-serif-tc text-lg tracking-[0.16em] text-slate-700">備註</legend>
            <p className="text-[12px] leading-6 text-slate-400">
              訂單送出後，將寄送 Email 通知，確認我們已收到您的訂單資訊。完成付款確認後，將依訂單順序安排出貨。
            </p>
            <label className="block">
              <span className="mb-2 block text-[12px] tracking-[0.12em] text-slate-400">其他備註（選填）</span>
              <textarea
                value={form.note}
                onChange={(event) => update("note", event.target.value)}
                rows={4}
                className="w-full border border-slate-300 bg-white/70 px-3 py-3 text-[13px] text-slate-700 outline-none transition focus:border-slate-500"
              />
            </label>
          </fieldset>

          <div className="flex items-end justify-between border-t border-slate-200 pt-6">
            <p className="text-[12px] tracking-[0.16em] text-slate-400">訂購金額</p>
            <p className="font-display text-[28px] text-slate-700">NT$ {totalAmount.toLocaleString("zh-TW")}</p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full bg-[#2b4456] text-[12px] tracking-[0.32em] text-white transition hover:bg-[#1d3342] disabled:opacity-60"
          >
            {submitting ? "送出中…" : "確認送出訂單"}
          </button>
          <p className="text-center text-[11px] tracking-[0.12em] text-slate-400">送出前請確認所有資料正確無誤</p>
        </form>
      </div>
    </section>
  )
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  inputMode,
  maxLength,
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  type?: string
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"]
  maxLength?: number
  placeholder?: string
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-[12px] tracking-[0.12em] text-slate-400">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full border border-slate-300 bg-white/70 px-3 text-[13px] text-slate-700 outline-none transition focus:border-slate-500"
      />
    </label>
  )
}

export default OrderForm
